import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { isApiError } from "@/services/Api";
import { caseClientUiConfigService, caseService, userSessionService } from "@/services";
import { ioaOverlapsAgreementPeriod } from "@/services/UtilitiesService";

import { useIoaStore } from "@/stores/ioa-store";
import { useTagStore } from "@/stores/tags-store";
import { useBrandStore } from "@/stores/brand-store";
import { useNotesStore } from "@/stores/notes-store";
import { usePointStore } from "@/stores/point-store";
import { useFiltersStore } from "@/stores/filters-store";
import { useMessageStore } from "@/stores/message-store";
import { useSnippetStore } from "@/stores/snippets-store";
import { useEvidenceStore } from "@/stores/evidence-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useQuarantineStore } from "@/stores/quarantine-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useCaseSecurityStore } from "@/stores/case-security-store";
import { useEvidenceCountStore } from "@/stores/evidence-count-store";
import { resetScopedStores } from "@/stores/plugins/scope-plugin";

import type AgreementPeriod from "@/models/AgreementPeriod";
import type CaseViewerConfig from "@/models/CaseViewerConfig";
import type InstanceOfAdvice from "@/models/InstanceOfAdvice";
import type { MigrationPlan, PlanRequest, PlanResponse } from "@/models/RecordMigration";
import { v4 } from "uuid";

export const useCaseStore = defineStore("case", {
  scope: "case",
  state: (): State => ({
    _loadingCaseName: undefined,
    checksets: [],
    config: { agreementPeriods: [] },
    moduleStatus: ModuleStatus.Initial,
    sessionLinkId: undefined,
    migrationPlans: [],
  }),
  getters: {
    selectedCaseName(): string | undefined {
      return this.currentRoute.params.casename as string | undefined;
    },
    isMigrationSource(): boolean {
      return this.migrationPlans.some(
        (plan) => plan.sourceCaseReference.toLowerCase() === this.selectedCaseName?.toLowerCase(),
      );
    },
    isMigrationDestination(): boolean {
      return this.migrationPlans.some(
        (plan) => plan.destinationCaseReference.toLowerCase() === this.selectedCaseName?.toLowerCase(),
      );
    },
  },
  actions: {
    async onCaseNameChange(): Promise<void> {
      const caseName = this.selectedCaseName;
      if (this._loadingCaseName === caseName && this.moduleStatus === ModuleStatus.Loading) return;

      this._loadingCaseName = caseName;
      this.setCaseStatus(ModuleStatus.Loading);

      try {
        await this.onCaseReferenceLoaded();
        await useCaseFileStore().onSelectedFileChange();
      } catch (e) {
        console.error(e);
      }

      if (this.selectedCaseName !== caseName) return;

      this._loadingCaseName = undefined;
      this.setCaseStatus(ModuleStatus.Ready);
    },
    async updateAgreementPeriod(arg: AgreementPeriod | AgreementPeriod[]): Promise<void> {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || this.selectedCaseName == null) return;

      const map: Record<string, AgreementPeriod> = {};
      for (const period of this.config.agreementPeriods) map[period.id] = period;
      for (const period of Array.isArray(arg) ? arg : [arg]) map[period.id] = period;

      const agreementPeriods = Object.values(map);

      await caseClientUiConfigService
        .Save(selectedClient, selectedBrand, this.selectedCaseName, { agreementPeriods })
        .then(() => this.setCaseConfig({ agreementPeriods }));
    },
    async removeIoaFromAgreementPeriods(instanceReference: string): Promise<void> {
      let shouldUpdate = false;
      const cleaned: AgreementPeriod[] = [];

      for (const period of this.config.agreementPeriods) {
        if (!(period.ioas?.includes(instanceReference) ?? false)) cleaned.push(period);
        else {
          shouldUpdate = true;
          cleaned.push({ ...period, ioas: period.ioas!.filter((ref) => ref !== instanceReference) });
        }
      }

      if (shouldUpdate) await this.updateAgreementPeriod(cleaned);
    },
    async sanitizeAgreementPeriodIoaAssociations(): Promise<void> {
      const ioaStore = useIoaStore();
      if (ioaStore.moduleStatus !== ModuleStatus.Ready) return;

      const ioaMap = new Map<string, InstanceOfAdvice>();
      for (const ioa of ioaStore.instanceOfAdvices) ioaMap.set(ioa.instanceReference, ioa);

      let shouldUpdate = false;
      const cleaned = this.config.agreementPeriods.map((period) => {
        const ioas: string[] = [];

        period.ioas?.forEach((ref) => {
          if (ioaMap.has(ref) && ioaOverlapsAgreementPeriod(period, ioaMap.get(ref)!)) ioas.push(ref);
          else shouldUpdate = true;
        });

        return { ...period, ioas };
      });

      if (shouldUpdate) await this.updateAgreementPeriod(cleaned);
    },
    async deleteAgreementPeriod(agreementPeriod: AgreementPeriod): Promise<void> {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null || this.selectedCaseName == null) return;

      const agreementPeriods = [...new Set(this.config.agreementPeriods)];
      const index: number = this.config.agreementPeriods.findIndex((f) => f.id === agreementPeriod.id);
      if (index < 0) {
        return;
      } else {
        agreementPeriods.splice(index, 1);
      }

      await caseClientUiConfigService
        .Save(selectedClient, selectedBrand, this.selectedCaseName, { agreementPeriods })
        .then(() => this.setCaseConfig({ agreementPeriods }));
    },
    async getCaseCheckSets(): Promise<void> {
      try {
        const caseReference = this.selectedCaseName;
        const { selectedBrand, selectedClient } = useBrandStore();

        if (caseReference == null || selectedClient == null || selectedBrand == null) return;

        const ccs = await caseService.caseCheckSets(selectedClient, selectedBrand, caseReference);
        this.setCaseCheckSets(ccs);
      } catch (e) {
        if (isApiError(e) && e.status === 204) {
          this.setCaseCheckSets([]);
          return;
        }

        useMessageStore().updateMessage({
          type: "error",
          title: "Case Check Sets",
          message: "Error Loading Case Check Sets",
          autoHide: false,
          showReload: true,
        });

        throw e;
      }
    },
    async getCaseConfig(): Promise<void> {
      try {
        const caseReference = this.selectedCaseName;
        const { selectedBrand, selectedClient } = useBrandStore();
        if (caseReference == null || selectedClient == null || selectedBrand == null) return;

        const caseConfig = await caseClientUiConfigService.Get(selectedClient, selectedBrand, caseReference);
        this.setCaseConfig(caseConfig);
      } catch (e) {
        if (isApiError(e) && e.status === 204) {
          this.setCaseConfig(undefined);
          return;
        }

        useMessageStore().updateMessage({
          type: "error",
          title: "Case Configuration",
          message: "Error Loading Case Configuration",
          autoHide: false,
          showReload: true,
        });

        throw e;
      }
    },

    async loadMigrationPlans(): Promise<void> {
      const caseReference = this.selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (caseReference == null || selectedClient == null || selectedBrand == null) return;

      try {
        const caseMetadata = await caseService.GetCaseMetadata(selectedClient, selectedBrand, caseReference);
        const migrations = caseMetadata.value.migration;
        this.migrationPlans =
          migrations != null && typeof migrations === "object"
            ? Object.values(migrations as Record<string, MigrationPlan>)
            : [];
      } catch (e) {
        this.migrationPlans = [];
        if (!isApiError(e) || (e.status !== 404 && e.status !== 204)) console.error(e);
      }
    },

    async planRecordMigration(request: PlanRequest): Promise<PlanResponse> {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null) {
        throw new Error("Cannot plan record migration without a selected brand and client.");
      }

      return await caseService.planRecordMigration(selectedClient, selectedBrand, request);
    },

    async migrateCaseRecords(plan: MigrationPlan, token: string): Promise<void> {
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedBrand == null || selectedClient == null) {
        throw new Error("Cannot migrate case records without a selected brand and client.");
      }

      await caseService.migrateRecords(selectedClient, selectedBrand, { plan, token });
    },

    // Mutations
    async onCaseReferenceLoaded() {
      const caseReference = this.selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      this.sessionLinkId = v4();
      if (caseReference != null && selectedClient != null && selectedBrand != null) {
        // We are setting a case reference, we could load case specific data
        /* This would split it out from pages and allow the store to build itself rather than being
           triggered from individual pages */
        const ioaStorePromise = useIoaStore().load();
        const caseConfigPromise = this.getCaseConfig();
        const caseCheckSetsPromise = this.getCaseCheckSets();

        const promises = [
          useEvidenceStore().loadCategories(),
          useEvidenceStore().loadEvidence(),
          useMetadataStore().load(),
          ioaStorePromise,
          useAnnotationStore().load(),
          useTagStore().load(),
          useQuarantineStore().load(),
          useSnippetStore().load(),
          useNotesStore().load(),
          useEvidenceCountStore().load(),
          useCaseSecurityStore().load(),
          this.loadMigrationPlans(),
          caseConfigPromise,
          caseCheckSetsPromise.then(() => usePointStore().load()),
          Promise.all([ioaStorePromise, caseConfigPromise]).then(() => this.sanitizeAgreementPeriodIoaAssociations()),
          userSessionService.setAgentConversationContext(this.sessionLinkId, {
            clientName: selectedClient,
            brand: selectedBrand,
            caseReference,
          }),
        ];

        await Promise.allSettled(promises);

        useFiltersStore().selectFilterSet(undefined);
        return;
      }

      // We are removing a case, we should clear the case specific data
      resetScopedStores("case");
      useFiltersStore().reset();
    },
    setCaseConfig(payload: CaseViewerConfig | undefined): void {
      this.config = payload ?? { agreementPeriods: [] };
    },
    setCaseStatus(payload: ModuleStatus): void {
      this.moduleStatus = payload;
    },
    setCaseCheckSets(checkSets: string[]): void {
      const configuredCheckSets = useBrandStore().viewerConfig?.checks?.sets;

      // Null check sets means just use what is attached
      // Empty arrays just use what is attached as well
      // Empty strings, just use what is attached as well
      if (
        configuredCheckSets == null ||
        (Array.isArray(configuredCheckSets) && configuredCheckSets.length === 0) ||
        configuredCheckSets === ""
      ) {
        this.checksets = checkSets;
        return;
      }

      // Get the checks sets included in the array or in the checkset string
      const filteredCheckSets = checkSets.filter((cs) => {
        if (Array.isArray(configuredCheckSets)) {
          return configuredCheckSets.some((ccs) => ccs.toLowerCase() === cs.toLowerCase());
        } else {
          return cs.toLowerCase() === configuredCheckSets.toLowerCase();
        }
      });

      // We have checksets attached to the case, but they will be hidden
      // Pop a console warning out so we can track why they are not showing easily
      if (filteredCheckSets.length === 0 && checkSets.length > 0) {
        console.warn(
          "No check sets are configured to be shown, but there are check sets attached to the case. Please check your configuration.",
        );
      }

      this.checksets = filteredCheckSets;
    },
    reset(): void {
      this.config = { agreementPeriods: [] };
      this.checksets = [];
      this.migrationPlans = [];
    },
  },
});
interface State {
  _loadingCaseName: string | undefined;
  sessionLinkId: string | undefined;
  checksets: string[];
  config: CaseViewerConfig;
  moduleStatus: ModuleStatus;
  migrationPlans: MigrationPlan[];
}
